import ArticleLayout, { H2, H3, Lead, Note, BackHome } from "@/components/ArticleLayout";
import { IMAGES } from "@/lib/config";

export default function PathologiesHumidite() {
    return (
        <ArticleLayout
            slug="pathologies-humidite"
            title={<>Les pathologies d'humidité <span className="italic text-brand">dans le bâti ancien</span></>}
            metaTitle="Les pathologies d'humidité dans le bâti ancien"
            subtitle="Remontées capillaires, condensation, infiltrations, salpêtre : comprendre l'origine du désordre avant d'engager le moindre travaux."
            breadcrumbLabel="Pathologies d'humidité"
            metaDescription="Guide complet des pathologies d'humidité dans les maisons anciennes (pierre, torchis, pisé) : remontées capillaires, condensation, salpêtre. Diagnostic et solutions respectueuses par un artisan diagnostiqueur en Eure-et-Loir."
            coverImage={IMAGES.hero}
            coverAlt="Mur ancien en pierre présentant des traces d'humidité et de salpêtre"
            updated="2026-01-15"
        >
            <Lead>
                90 % des désordres visibles sur un mur ancien viennent d'un problème d'eau. Comprendre <em>d'où</em>
                elle vient et <em>où</em> elle s'accumule est la première étape — et souvent la seule qui compte vraiment.
            </Lead>

            <H2>Pourquoi les murs anciens gèrent l'eau différemment</H2>
            <p>
                Un mur en pierre, en pisé, en bauge ou en torchis n'est pas étanche : il <strong>respire</strong>. Il
                absorbe l'humidité, la transporte et la restitue par évaporation. Ce cycle est vertueux tant
                que rien ne le bloque. À l'inverse, un mur moderne s'appuie sur des barrières étanches (film,
                ciment, peinture acrylique). Traiter un mur ancien comme un mur moderne, c'est piéger l'eau
                à l'intérieur et déclencher tous les désordres suivants.
            </p>

            <H2>Les 4 pathologies les plus fréquentes</H2>

            <H3>1. Les remontées capillaires</H3>
            <p>
                L'eau du sol monte dans les murs par les micro-canaux de la pierre ou du mortier, jusqu'à un
                mètre voire davantage. Les symptômes visibles : traces sombres en bas de mur, décollement
                d'enduits, cristaux blancs (salpêtre), papiers peints qui cloquent.
            </p>
            <p>
                <strong>Ce qu'il faut faire :</strong> identifier la source (drainage périphérique, remontée
                sous dallage, capillarité de la pierre), rétablir la ventilation basse du mur, remplacer les
                enduits ciment par de la chaux, et — souvent — reprendre le pied de mur.
            </p>

            <H3>2. La condensation</H3>
            <p>
                L'humidité de l'air se dépose sur les surfaces les plus froides du logement (angles, ponts
                thermiques, murs nord). C'est la pathologie la plus mal diagnostiquée : elle est souvent
                confondue avec une infiltration.
            </p>
            <p>
                <strong>Ce qu'il faut faire :</strong> ventiler mieux (VMC hygroréglable ou ventilation
                naturelle assainie), corriger les ponts thermiques, chauffer plus régulièrement, appliquer
                un enduit chaux-chanvre correcteur d'hygrométrie.
            </p>

            <H3>3. Les infiltrations</H3>
            <p>
                Une couverture, une gouttière, une souche de cheminée ou un rejingot défaillants amènent l'eau
                de pluie directement dans le mur. Les traces sont localisées, saisonnières et souvent en
                hauteur.
            </p>

            <H3>4. Le salpêtre (efflorescences salines)</H3>
            <p>
                Le salpêtre n'est pas une cause : c'est un symptôme. Les sels contenus dans le sol ou dans le
                mur sont transportés par l'eau, cristallisent en surface et détruisent l'enduit. Le gratter
                sans traiter la cause, c'est le voir revenir dans les mois qui suivent.
            </p>

            <Note>
                <strong>Le réflexe à éviter :</strong> le traitement chimique par injection de résines
                hydrofuges. Sur un mur ancien, cela déplace le problème vers le haut, fragilise la
                maçonnerie et bloque définitivement la migration de vapeur d'eau. Coût élevé, résultat
                souvent médiocre.
            </Note>

            <H2>Le diagnostic : la seule étape qui ne se saute pas</H2>
            <p>
                Un diagnostic complet du bâti ancien passe par :
            </p>
            <ul className="list-disc pl-6 space-y-2">
                <li>Une lecture du bâti (époque, techniques constructives, historique des interventions)</li>
                <li>Des mesures d'humidité par sondes ou testeurs capacitifs à plusieurs hauteurs et points</li>
                <li>L'analyse de la ventilation et du chauffage</li>
                <li>L'inspection périphérique (drainage, terrasse, couverture, gouttières)</li>
                <li>Un rapport écrit détaillé + préconisations hiérarchisées</li>
            </ul>

            <H2>Ce qui suit le diagnostic : la hiérarchisation</H2>
            <p>
                Sur une maison ancienne, on ne fait <strong>jamais</strong> tout en même temps. On rétablit
                d'abord l'écoulement des eaux extérieures, puis on assainit le pied de mur, puis on ventile,
                puis — et seulement ensuite — on refait les enduits intérieurs. Court-circuiter cet ordre,
                c'est refaire deux fois les mêmes travaux.
            </p>

            <H2>En pratique — Eure-et-Loir, Perche, Île-de-France</H2>
            <p>
                J'interviens en tant que diagnostiqueur indépendant sur les maisons anciennes de la région
                (Saint-Lubin-des-Joncherets, Dreux, Nonancourt, Verneuil-sur-Avre, Chartres, L'Aigle,
                Évreux…). Le diagnostic sur site prend une demi-journée et inclut le rapport écrit.
            </p>

            <Note tone="sand">
                📞 Pour un premier avis téléphonique gratuit :{" "}
                <a href="tel:+33664208134" className="text-brand hover:underline font-medium">06 64 20 81 34</a>.
            </Note>

            <BackHome />
        </ArticleLayout>
    );
}
